import React, { useCallback, useEffect, useMemo, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { FileText, FolderOpen } from "lucide-react"
import { cn } from "../lib/utils"
import { appConfirm } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.error ? `${data.error}` : `Request failed (${res.status})`
    throw new Error(message)
  }
  return data
}

const FOLDERS = [
  { slug: "library", label: "General" },
  { slug: "invoices", label: "Invoices" },
  { slug: "bills", label: "Bills" },
  { slug: "contracts", label: "Contracts" },
]

function TextInput({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      />
    </label>
  )
}

function SelectInput({ label, value, onChange, options }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function DocumentItemsTable({ folderFilter, showForm }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ name: "", folder_slug: folderFilter, content_type: "", size_bytes: "0", notes: "" })

  const listUrl = useMemo(() => {
    const base = "/api/documents/items"
    return folderFilter ? `${base}?folder=${encodeURIComponent(folderFilter)}` : base
  }, [folderFilter])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const data = await fetchJson(listUrl)
      setItems(data || [])
    } catch (e) {
      setError(e.message || "Load failed")
    } finally {
      setLoading(false)
    }
  }, [listUrl])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!editingId) setForm((f) => ({ ...f, folder_slug: folderFilter }))
  }, [folderFilter, editingId])

  async function onSubmit(e) {
    e.preventDefault()
    try {
      setError("")
      const payload = {
        name: form.name.trim(),
        folder_slug: form.folder_slug,
        content_type: form.content_type.trim() || null,
        size_bytes: Number.parseInt(String(form.size_bytes || "0"), 10) || 0,
        notes: form.notes.trim() || null,
      }
      const body = JSON.stringify({ document_item: payload })
      if (editingId) {
        await fetchJson(`/api/documents/items/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson("/api/documents/items", { method: "POST", body })
      }
      await load()
      setEditingId(null)
      setForm({ name: "", folder_slug: folderFilter, content_type: "", size_bytes: "0", notes: "" })
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  async function onDelete(id) {
    if (!(await appConfirm("Delete this document record?", { title: "Delete document", danger: true, confirmLabel: "Delete" }))) return
    try {
      await fetchJson(`/api/documents/items/${id}`, { method: "DELETE" })
      await load()
      if (editingId === id) {
        setEditingId(null)
        setForm({ name: "", folder_slug: folderFilter, content_type: "", size_bytes: "0", notes: "" })
      }
    } catch (e) {
      setError(e.message || "Delete failed")
    }
  }

  function hydrate(item) {
    setEditingId(item.id)
    setForm({
      name: item.name || "",
      folder_slug: item.folder_slug || folderFilter,
      content_type: item.content_type || "",
      size_bytes: String(item.size_bytes ?? 0),
      notes: item.notes || "",
    })
  }

  const filtered = useMemo(() => {
    if (!query.trim()) return items
    const q = query.trim().toLowerCase()
    return items.filter((it) => [it.name, it.notes, it.folder_slug].some((f) => String(f || "").toLowerCase().includes(q)))
  }, [items, query])

  return (
    <div className="flex flex-col gap-6">
      {showForm ? (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>{editingId ? "Edit document" : "Register document"}</CardTitle>
            {editingId ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditingId(null)
                  setForm({ name: "", folder_slug: folderFilter, content_type: "", size_bytes: "0", notes: "" })
                }}
              >
                Cancel
              </Button>
            ) : null}
          </CardHeader>
          <CardContent>
            {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
            <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
              <TextInput label="Display name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="Invoice_ABC.pdf" />
              <SelectInput
                label="Folder"
                value={form.folder_slug}
                onChange={(v) => setForm((f) => ({ ...f, folder_slug: v }))}
                options={FOLDERS}
              />
              <TextInput label="Content type (optional)" value={form.content_type} onChange={(v) => setForm((f) => ({ ...f, content_type: v }))} placeholder="application/pdf" />
              <TextInput label="Size (bytes)" value={form.size_bytes} onChange={(v) => setForm((f) => ({ ...f, size_bytes: v }))} type="number" />
              <div className="md:col-span-2">
                <TextInput label="Notes" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} placeholder="Linked to bill #…" />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button type="submit">{editingId ? "Save" : "Add"}</Button>
                <Button type="button" variant="ghost" onClick={() => load()}>
                  Refresh
                </Button>
              </div>
            </form>
            <p className="mt-3 text-xs text-muted-foreground">
              Phase 1 stores metadata only (Zoho-style library before binary storage). File upload attaches in a later phase.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Documents</CardTitle>
          <div className="text-sm text-muted-foreground">{loading ? "Loading…" : `${filtered.length} record(s)`}</div>
        </CardHeader>
        <CardContent>
          <div className="mb-3">
            <TextInput label="Search" value={query} onChange={setQuery} placeholder="Filter by name…" />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Folder</th>
                  <th className="py-2 pr-4">Size</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-border/60 hover:bg-muted/15">
                    <td className="py-2 pr-4 font-medium text-foreground/85">{it.name}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{FOLDERS.find((f) => f.slug === it.folder_slug)?.label || it.folder_slug}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{it.size_bytes}</td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <Button type="button" variant="ghost" className="h-8 px-2" onClick={() => hydrate(it)}>
                          Edit
                        </Button>
                        <Button type="button" variant="ghost" className="h-8 px-2 text-red-600" onClick={() => onDelete(it.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-muted-foreground">
                      No documents in this folder yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function initialFolderFromUrl() {
  if (typeof window === "undefined") return "library"
  const q = new URLSearchParams(window.location.search).get("folder")
  return FOLDERS.some((f) => f.slug === q) ? q : "library"
}

function FolderOverview() {
  const [counts, setCounts] = useState({})
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)

  useEffect(() => {
    let alive = true
    async function run() {
      try {
        const next = {}
        for (const f of FOLDERS) {
          const rows = await fetchJson(`/api/documents/items?folder=${encodeURIComponent(f.slug)}`)
          if (!alive) return
          next[f.slug] = (rows || []).length
        }
        if (alive) setCounts(next)
      } finally {
        if (alive) setLoading(false)
      }
    }
    run()
    return () => {
      alive = false
    }
  }, [])

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {FOLDERS.map((f) => (
        <Card key={f.slug}>
          <CardHeader className="flex flex-row items-center gap-2">
            <FolderOpen className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">{f.label}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">{loading ? "…" : `${counts[f.slug] ?? 0} file(s)`}</p>
            <a
              href={`/documents/library?folder=${encodeURIComponent(f.slug)}`}
              className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-card px-3 py-1.5 text-[14px] font-medium text-foreground/85 hover:bg-muted/40"
            >
              Open
            </a>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export function DocumentsPage(props) {
  const {
    active_slug,
    section,
    section_title,
    user_name,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)
  const key = section || "library"
  const [folderNav, setFolderNav] = useState(initialFolderFromUrl)

  useEffect(() => {
    if (key !== "library") return
    setFolderNav(initialFolderFromUrl())
  }, [key])

  const phaseCopy = useMemo(
    () => ({
      library:
        "Document library (Zoho-style phase 1): register files you store elsewhere; metadata and folders now, binary upload in the next phase.",
      folders: "Browse by folder — same mental model as Zoho’s left-rail folders.",
      upload: "Quick register form — mirrors Zoho’s upload entry point before attachment pipeline is wired.",
    }),
    []
  )

  return (
    <BookShell activeSlug={active_slug || "documents"} userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader
          context="Documents"
          title={section_title}
          description={phaseCopy[key] || phaseCopy.library}
          actions={
            key === "library" ? (
              <div className="flex flex-wrap gap-2">
                {FOLDERS.map((f) => (
                  <button
                    key={f.slug}
                    type="button"
                    onClick={() => setFolderNav(f.slug)}
                    className={cn(
                      "rounded-md border px-3 py-1.5 text-sm",
                      folderNav === f.slug ? "border-primary/30 bg-primary/[0.06] text-primary" : "border-border bg-card text-foreground/80 hover:bg-muted/40",
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            ) : null
          }
        />

        {key === "library" ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="text-sm">Viewing folder: {FOLDERS.find((f) => f.slug === folderNav)?.label}</span>
          </div>
        ) : null}

        {key === "library" ? <DocumentItemsTable folderFilter={folderNav} showForm /> : null}

        {key === "folders" ? (
          <>
            <FolderOverview />
            <p className="text-xs text-muted-foreground">Open sends you to the library with the correct folder (query string), same flow as Zoho folder shortcuts.</p>
          </>
        ) : null}

        {key === "upload" ? <DocumentItemsTable folderFilter="library" showForm /> : null}
      </div>
    </BookShell>
  )
}
